<script lang="ts" setup>
import { ref, onUnmounted } from 'vue'

const emit = defineEmits(['snapshot'])
const error = ref<string | null>(null)
const multicamera = ref<boolean>(false)
const size = window.innerHeight - 400;
const video = ref<HTMLVideoElement>();
const stream = ref<MediaStream>();
const canvas = ref<HTMLCanvasElement>();
const loaded = ref(false);
let availableDevices: MediaDeviceInfo[] = [];
let currentDeviceIndex: number = 0;
const constraints = {
  video: {
    width: size,
    height: size,
    facingMode: 'environment',
    deviceId: {} as any,
  },
  audio: false
}
const devices = async ( kinds: MediaDeviceKind[] = ["videoinput"]) => {
    if (!('mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices)) {
        error.value = 'Camera unavailable, please check your app permissions and make sure that the camera is enabled.';
        return;
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    availableDevices = devices.filter((device) => kinds.includes(device.kind));
};
const snapshot = (
    type = "image/jpeg",
    quality = 0.8
): Promise<Blob | null> => {
    if (!video.value) throw new Error("Video ref is null");
    if (!canvas.value) throw new Error("Canvas ref is null");

    canvas.value.width = size;
    canvas.value.height = size;

    canvas.value
        .getContext("2d")
        ?.drawImage(video.value, 0, 0, size, size);

    return new Promise((resolve) => {
        canvas.value?.toBlob(
            (blob: Blob | null) => {
                emit("snapshot", blob);
                resolve(blob);
            },
            type,
            quality
        );
    });
};
const changeCamera = async () => {
    stop();
    currentDeviceIndex++
    if (currentDeviceIndex > availableDevices.length - 1) currentDeviceIndex = 0;
    constraints.video.deviceId.exact = availableDevices[currentDeviceIndex].deviceId;
    await start();
}
const takePicture = async () => {
    await snapshot()
}
const stop = (): void => {
    stream.value?.getTracks().forEach((track) => track.stop());
};
const start = async ()=>{
    stop();
    loaded.value = false;
    const permissionState = await navigator.permissions.query({ name: "camera" as PermissionName });
    if (permissionState.state == 'denied') {
        error.value = 'Camera permission was denied. Please allow the camera permission to use this feature.';
        return;
    }
    
    
    if ('mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices) {
        stream.value = await navigator.mediaDevices.getUserMedia(constraints);
        if (stream.value == null) {
            error.value = 'Camera was not found or was busy. Please make sure that the camera is connected and not used by another application.';
            return;
        }
        if (video.value == undefined){
            error.value = 'Video was not found';
            return;
        }
        video.value.srcObject = stream.value;
        await video.value.play().catch(() => {});
    } else {
        if (permissionState.state == 'granted') {
            error.value = "Camera unavailable. No cameras were found, please check you camera is not already in use.";
        } else {
            error.value = 'Camera unavailable. No cameras were found, please check your app permissions and make sure that the camera is enabled.'
        }
        
    }
    await devices();
    loaded.value = true;

}
onUnmounted(() => {
    stop();
})

defineExpose({ start, snapshot, changeCamera });
</script>

<template>
    <el-alert type="error" center  v-if="error != null">{{error}} </el-alert>
    <div class="camera-container" :style="{maxHeight: size + 'px'}">
        <div v-if="!loaded" class="loading">
            Starting camera...
        </div>
        <video class="camera-video" autoplay="true" ref="video" muted="true" webkit-playsinline playsinline="true"></video>
        <button v-if="loaded" class="center-button" @click="takePicture">
            <i class="fas fa-camera"></i>
        </button>
        <button class="left-button" v-if="multicamera && loaded" @click="changeCamera">
            <i class="fas fa-sync-alt"></i>
        </button>
    </div>
    <canvas ref="canvas" class="camera-canvas"></canvas>
</template>

<style scoped>
.center-button{
    position: absolute;
    border-radius: 50%;
    height: 90px;
    width: 90px;
    bottom: 20px;
    left: 50%;
    background:transparent;
    margin-left:-45px;
    border: 3px solid #fff;
    z-index:99999999;
    display: flex;
    align-items: center;
    justify-content: center;
}
.center-button i {
    color: #000;
    font-size: 2.5em;
    background:#fff;
    border-radius: 50%;
    height: 60px;
    width: 60px;
    line-height: 60px;
}
.left-button{
    position: absolute;
    color: #000;
    border-radius: 50%;
    height: 40px;
    width: 40px;
    bottom: 35px;
    left: 50%;
    margin-left: 70px;
    border: 3px solid transparent;
}
.loading{
    text-align: center;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -55px;
}

.camera-container {
    position: relative;
    width: 100%;
    height: 100%;
}

.camera-video {
    width: 100%;
    height: 100%;
}

.camera-canvas {
    display: none;
}
</style>